import { useEffect, useState } from "react";
import {
  PUAN_HAREKETI_ETIKETI,
  PUAN_UYGUNLUGU_ETIKETI,
  type BekleyenOzet,
  type OyunKurali,
  type OyunOgrenciOzeti,
} from "@koclukpanel/contracts";
import { Buton, Ikon, Iskelet, SayfaBasligi, cn, type IkonAdi } from "@koclukpanel/ui";
import type { OdulHedefi } from "./Kabuk";
import { ApiHatasi, instagramTesekkuru, oyunOzetim } from "./api";
import { hataMetni } from "./hata-metni";
import { Cark } from "./Cark";
import { Davet } from "./Davet";
import { Liderlik } from "./Liderlik";
import { Magaza } from "./Magaza";
import { Siparislerim } from "./Siparislerim";

/**
 * ÖDÜLLER — F-GAM-15 (oyunlaştırma dalga 1). Gelişim sekmesinin bir görünümü (mutabakat: yeri Gelişim).
 *
 * Bütün sayılar SUNUCUDAN: bakiye defterin toplamı, bekleyen puan kesinleşmeyle AYNI hesaptan. Ekran hiçbir puanı
 * kendisi hesaplamıyor — hesaplasaydı Pazartesi kesinleşen sayıyla ayrışabilirdi.
 *
 * Instagram kartı F-GAM-01 istisnası: "takip ettim" demek yeter, doğrulanmaz ve ekran "doğrulandı" DEMEZ. Bağlantı
 * firmaya ait olduğu için koda gömülmez (`VITE_INSTAGRAM_URL`); tanımlı değilse kart görünmez.
 */
// `marka.ts` ile ayni okuma kalibi: Vite derleme aninda enjekte eder; tanimli degilse bos. Test adresi prop'la
// verir (ortam degiskenini test icinde degistirmek bu okuma kalibina ulasmiyordu).
function instagramAdresi(): string | undefined {
  return (
    (import.meta as unknown as { env?: Record<string, string | undefined> }).env
      ?.VITE_INSTAGRAM_URL || undefined
  );
}

function tarihMetni(iso: string): string {
  const [y, a, g] = iso.slice(0, 10).split("-");
  return `${g}.${a}.${y}`;
}

function gunAdi(iso: string): string {
  return new Intl.DateTimeFormat("tr-TR", { weekday: "long", timeZone: "UTC" }).format(
    new Date(`${iso}T00:00:00Z`),
  );
}

interface OdulKarti {
  gorunum: Exclude<OdulHedefi, "oduller">;
  baslik: string;
  aciklama: string;
  ikon: IkonAdi;
  /** Kartın açık zemini ve ikon karosunun degradesi — sınıf adları TAM yazılı (Tailwind taraması). */
  zemin: string;
  karo: string;
}

/**
 * ÖDÜL MERKEZİ KARTLARI — UNİKOÇ "ÖDÜL MERKEZİ" bölümünün telefondaki karşılığı.
 *
 * 9b'ye kadar bu beş ekran Gelişim → Ödüller altında, altı eşit gri düğmeden oluşan bir seçici
 * satırındaydı ("menü çok karışık" şikâyetinin ölçülen kaynağı: iki seviye seçici). Artık her biri
 * büyük, renkli, iki sütunlu bir kart: ikonu, adı ve NE İŞE YARADIĞI tek bakışta okunuyor.
 *
 * RENK METİNDE DEĞİL ZEMİNDE: durum renklerinin (yeşil/turuncu/mavi) dolu hâli beyaz yazıyla AA'yı
 * geçmiyor (DurumRozeti notu). Kart zemini aynı ailenin AÇIK tonu, yazı koyu; renkli olan ikon karosu.
 */
const ODUL_KARTLARI: readonly OdulKarti[] = [
  {
    gorunum: "magaza",
    baslik: "Mağaza",
    aciklama: "Puanlarınla hediye al",
    ikon: "canta",
    zemin: "bg-mor-50 ring-mor-kenar",
    karo: "from-birincil-bas to-birincil-son",
  },
  {
    gorunum: "cark",
    baslik: "Bonus Çarkı",
    aciklama: "24 saatte bir çevir, puan kazan",
    ikon: "cark",
    zemin: "bg-dersden-zemin ring-dersden-nokta/25",
    karo: "from-dersden-nokta to-birincil-son",
  },
  {
    gorunum: "liderlik",
    baslik: "Liderlik",
    aciklama: "Geçen haftanın puan tablosu",
    ikon: "kupa",
    zemin: "bg-bekliyor-zemin ring-bekliyor-nokta/30",
    karo: "from-bekliyor-nokta to-eylem-kirmizi",
  },
  {
    gorunum: "davet",
    baslik: "Davet Et",
    aciklama: "Arkadaşını davet et, ikiniz de kazanın",
    ikon: "ogrenci",
    zemin: "bg-tamam-zemin ring-tamam-nokta/25",
    karo: "from-tamam-nokta to-dersgeo-nokta",
  },
  {
    gorunum: "siparislerim",
    baslik: "Siparişlerim",
    aciklama: "Aldığın hediyelerin durumu",
    ikon: "arsiv",
    zemin: "bg-planli-zemin ring-planli-nokta/25",
    karo: "from-planli-nokta to-kaydet-son",
  },
];

/** Alt ekranın bant başlığı ve tek satır açıklaması (UNİKOÇ metinleriyle). */
const ALT_BASLIK: Record<Exclude<OdulHedefi, "oduller">, { baslik: string; aciklama: string }> = {
  magaza: { baslik: "Ödül Mağazası", aciklama: "Biriktirdiğin puanlarla hediyelerini al." },
  cark: { baslik: "Bonus Çarkı", aciklama: "Şansını dene, puanı kap!" },
  liderlik: {
    baslik: "Liderlik Tablosu",
    aciklama: "Aynı sınıftaki öğrencilerle haftalık puan sırası.",
  },
  davet: {
    baslik: "Davet Ettiklerim",
    aciklama: "Davet kodunu paylaş, arkadaşınla birlikte kazan.",
  },
  siparislerim: { baslik: "Sipariş Geçmişim", aciklama: "Mağazadan aldıklarının durumu." },
};

/**
 * ÖDÜLLER — F-GAM-15 · 7a · 7b. Sürüm 9b'den beri kendi alt şerit girdisi ("Ödüller").
 *
 * Merkez ("oduller"): Ödül Merkezi başlığı + beş büyük kart + Puanlarım (bakiye, bekleyen, rozetler,
 * hareketler). Puanlarım ayrı bir kart DEĞİL, merkezin gövdesi: öğrencinin bu bölümü açınca ilk
 * sorduğu "kaç puanım var".
 *
 * Görünüm dışarıdan sürülebiliyor (`gorunum`/`onGorunum`): masaüstü yan menüsü doğrudan "Mağaza"yı
 * açıyor. Görünüm değişince önceki ekranın durumu atılır: mağazada yarım kalan seçim sonradan eski
 * bakiyeyle açılmasın.
 */
export function Oduller({
  instagramUrl,
  gorunum: disGorunum,
  onGorunum,
}: {
  instagramUrl?: string;
  gorunum?: OdulHedefi;
  onGorunum?: (g: OdulHedefi) => void;
} = {}) {
  const [icGorunum, setIcGorunum] = useState<OdulHedefi>("oduller");
  const gorunum = disGorunum ?? icGorunum;
  const sec = (g: OdulHedefi) => {
    setIcGorunum(g);
    onGorunum?.(g);
  };

  if (gorunum === "oduller") {
    return (
      <div className="flex flex-col gap-4">
        <SayfaBasligi
          baslik="Ödül Merkezi"
          aciklama="Çalıştıkça puan kazan, puanlarını hediyeye çevir."
          ikon="hediye"
          ikonTuru="degrade"
        />
        <ul aria-label="Ödül Merkezi" className="grid grid-cols-2 gap-3 lg:grid-cols-5">
          {ODUL_KARTLARI.map((k, i) => (
            <li
              key={k.gorunum}
              className={cn(i === ODUL_KARTLARI.length - 1 && "max-lg:col-span-2")}
            >
              <button
                type="button"
                onClick={() => sec(k.gorunum)}
                className={cn(
                  "flex h-full min-h-[8.5rem] w-full flex-col items-start gap-3 rounded-[1.25rem] p-4 text-left ring-1 ring-inset",
                  "ease-cikis transition-[box-shadow,translate] duration-(--sure-anlik) hover:-translate-y-0.5 hover:shadow-(--golge-unk-kart)",
                  k.zemin,
                )}
              >
                <span
                  aria-hidden="true"
                  className={cn(
                    "text-notr-0 grid size-12 place-items-center rounded-[0.875rem] bg-linear-to-br shadow-(--golge-unk-birincil)",
                    k.karo,
                  )}
                >
                  <Ikon ad={k.ikon} className="size-6" />
                </span>
                <span className="flex flex-col gap-0.5">
                  <span className="text-metin-koyu text-base font-bold">{k.baslik}</span>
                  <span className="text-metin-ikincil text-xs leading-snug">{k.aciklama}</span>
                </span>
              </button>
            </li>
          ))}
        </ul>
        <h2 className="text-metin-koyu mt-1 text-lg font-bold">Puanlarım</h2>
        <Puanlarim instagramUrl={instagramUrl} />
      </div>
    );
  }

  const b = ALT_BASLIK[gorunum];
  return (
    <div className="flex flex-col gap-4">
      <div>
        <button
          type="button"
          onClick={() => sec("oduller")}
          className="min-h-dokunma text-mor-700 hover:bg-mor-50 inline-flex items-center gap-1.5 rounded-xl px-2 text-sm font-semibold"
        >
          <span aria-hidden="true">←</span> Ödül Merkezi
        </button>
      </div>
      <SayfaBasligi
        baslik={b.baslik}
        aciklama={b.aciklama}
        ikon={ODUL_KARTLARI.find((k) => k.gorunum === gorunum)?.ikon}
        ikonTuru="degrade"
      />
      {gorunum === "magaza" ? (
        <Magaza onSiparislerim={() => sec("siparislerim")} />
      ) : gorunum === "siparislerim" ? (
        <Siparislerim onMagaza={() => sec("magaza")} />
      ) : gorunum === "cark" ? (
        /*
          ÇARK SAHNESİ BİLEREK KOYU: "Klasik TV" çizimi Emre'nin onayıyla birebir (cark-tema.ts).
          Burada yalnız UNİKOÇ kart çerçevesine oturuyor — köşeli, kenardan kenara bir blok yerine
          yuvarlak köşeli bir kart. Sahne `-mx-4 -mb-4` ile kabın dolgusunu dolduruyor.
        */
        <div className="overflow-hidden rounded-[1.5rem] px-4 pb-4 shadow-(--golge-unk-kart)">
          <Cark />
        </div>
      ) : gorunum === "liderlik" ? (
        <Liderlik />
      ) : (
        <Davet />
      )}
    </div>
  );
}

function Puanlarim({ instagramUrl }: { instagramUrl?: string }) {
  const [ozet, setOzet] = useState<OyunOgrenciOzeti | null>(null);
  const [kapali, setKapali] = useState(false);
  const [hata, setHata] = useState<string | null>(null);
  const [yenile, setYenile] = useState(0);
  const instagram = instagramUrl ?? instagramAdresi();

  useEffect(() => {
    let iptal = false;
    void (async () => {
      try {
        const o = await oyunOzetim();
        if (!iptal) setOzet(o);
      } catch (e) {
        if (iptal) return;
        if (e instanceof ApiHatasi && e.durum === 404) setKapali(true);
        else setHata(hataMetni(e, "Ödüller alınamadı."));
      }
    })();
    return () => {
      iptal = true;
    };
  }, [yenile]);

  if (kapali) return <p className="text-notr-600 text-sm">Ödüller şu an kapalı.</p>;
  if (hata) {
    return (
      <p role="alert" className="rounded-alan bg-hata-50 text-hata-700 px-3 py-2 text-sm">
        {hata}
      </p>
    );
  }
  if (ozet === null) return <Iskelet satir={5} />;

  const bekleyenToplam = ozet.bekleyen.reduce((t, b) => t + b.toplam, 0);
  const baslamadi = ozet.baslangic !== null && ozet.baslangic > bugunAnahtari();

  return (
    <div className="flex flex-col gap-3">
      {ozet.sureUyarisi && (
        <p role="status" className="rounded-alan bg-uyari-50 text-uyari-800 px-3 py-2 text-sm">
          Uzun süredir puan kazanmadın. {tarihMetni(ozet.sureUyarisi.dusmeTarihi)} tarihine kadar
          bir puan kazanırsan puanların korunur.
        </p>
      )}

      <section
        aria-labelledby="puan-bakiye"
        className="rounded-kart border-notr-200 bg-notr-0 shadow-kart border p-4"
      >
        <h3 id="puan-bakiye" className="text-notr-600 text-sm font-medium">
          Puanın
        </h3>
        <p className="text-notr-900 text-3xl font-semibold tabular-nums">{ozet.bakiye}</p>
        {baslamadi ? (
          <p className="text-notr-600 mt-1 text-sm">
            Puanlar {tarihMetni(ozet.baslangic!)} Pazartesi başlıyor.
          </p>
        ) : (
          <p className="text-notr-600 mt-1 text-sm">
            Bekleyen:{" "}
            <span className="text-notr-900 font-medium tabular-nums">{bekleyenToplam}</span> —
            haftan bitince (Pazartesi) puanına eklenir.
          </p>
        )}
      </section>

      {ozet.bekleyen.map((b) => (
        <BekleyenHafta key={b.hafta} b={b} />
      ))}

      <section
        aria-labelledby="puan-rozetler"
        className="rounded-kart border-notr-200 bg-notr-0 shadow-kart border p-3"
      >
        <h3 id="puan-rozetler" className="text-notr-900 mb-2 text-sm font-semibold">
          Rozetlerin
        </h3>
        <ul className="grid grid-cols-2 gap-2">
          {ozet.rozetler.map((r) => (
            <li
              key={r.kod}
              className={cn(
                "rounded-alan border p-2",
                r.kazanildi
                  ? "border-marka-200 bg-marka-50"
                  : "border-notr-200 bg-notr-50 opacity-70",
              )}
            >
              <p className="text-notr-900 text-sm font-medium">{r.ad}</p>
              <p className="text-notr-600 text-xs">{r.aciklama}</p>
              <p className="text-notr-600 mt-1 text-xs">
                {r.kazanildi ? `Kazandın: ${tarihMetni(r.kazanildi)}` : "Henüz kazanılmadı"}
              </p>
            </li>
          ))}
        </ul>
      </section>

      <section
        aria-labelledby="puan-hareketler"
        className="rounded-kart border-notr-200 bg-notr-0 shadow-kart border p-3"
      >
        <h3 id="puan-hareketler" className="text-notr-900 mb-2 text-sm font-semibold">
          Son hareketler
        </h3>
        {ozet.sonHareketler.length === 0 ? (
          <p className="text-notr-600 text-sm">Henüz kesinleşmiş puanın yok.</p>
        ) : (
          <ul className="divide-notr-100 divide-y">
            {ozet.sonHareketler.map((h) => (
              <li key={h.id} className="flex items-start justify-between gap-2 py-2">
                <div>
                  <p className="text-notr-900 text-sm">{PUAN_HAREKETI_ETIKETI[h.tur] ?? h.tur}</p>
                  {h.not && <p className="text-notr-600 text-xs">{h.not}</p>}
                  <p className="text-notr-600 text-xs">{tarihMetni(h.tarih)}</p>
                </div>
                <span
                  className={cn(
                    "text-sm font-semibold tabular-nums",
                    h.miktar < 0 ? "text-hata-700" : "text-onay-700",
                  )}
                >
                  {h.miktar > 0 ? `+${h.miktar}` : h.miktar}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      {!ozet.kampanyaKatildi && instagram && (ozet.kural?.kampanya.instagram ?? 0) > 0 && (
        <InstagramKarti
          adres={instagram}
          miktar={ozet.kural!.kampanya.instagram}
          onBitti={() => setYenile((n) => n + 1)}
        />
      )}

      {ozet.kural && <NasilKazanilir k={ozet.kural} />}
    </div>
  );
}

function bugunAnahtari(): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Istanbul" }).format(new Date());
}

function BekleyenHafta({ b }: { b: BekleyenOzet }) {
  const gunler = new Map<string, BekleyenOzet["gorevler"]>();
  for (const g of b.gorevler) gunler.set(g.gun, [...(gunler.get(g.gun) ?? []), g]);
  return (
    <section
      aria-labelledby={`bekleyen-${b.hafta}`}
      className="rounded-kart border-notr-200 bg-notr-0 shadow-kart border p-3"
    >
      <h3 id={`bekleyen-${b.hafta}`} className="text-notr-900 text-sm font-semibold">
        {tarihMetni(b.hafta)} haftası — bekleyen {b.toplam}
      </h3>
      <p className="text-notr-600 text-xs">
        Görevler {b.gorevPuani} · haftalık uyum {b.haftaPuani} (tam görev oranın %
        {Math.round(b.oran * 100)}) · deneme {b.denemePuani}
      </p>
      {[...gunler.entries()].map(([gun, satirlar]) => (
        <div key={gun} className="mt-2">
          <p className="text-notr-700 text-xs font-medium capitalize">{gunAdi(gun)}</p>
          <ul>
            {satirlar.map((s) => (
              <li key={s.gorevId} className="text-notr-600 flex justify-between text-xs">
                <span>
                  {PUAN_UYGUNLUGU_ETIKETI[s.seviye]}
                  {s.gecGiris ? " (geç girildi, yarım puan)" : ""}
                </span>
                <span className="tabular-nums">{s.puan}</span>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </section>
  );
}

function InstagramKarti({
  adres,
  miktar,
  onBitti,
}: {
  adres: string;
  miktar: number;
  onBitti: () => void;
}) {
  const [gonderiliyor, setGonderiliyor] = useState(false);
  const [hata, setHata] = useState<string | null>(null);
  return (
    <section
      aria-labelledby="puan-instagram"
      className="rounded-kart border-notr-200 bg-notr-0 shadow-kart border p-3"
    >
      <h3 id="puan-instagram" className="text-notr-900 text-sm font-semibold">
        Bizi Instagram'da takip et
      </h3>
      <p className="text-notr-600 text-xs">
        Takip ettiğini söylemen yeterli; teşekkür olarak bir kez {miktar} puan eklenir.
      </p>
      <div className="mt-2 flex flex-wrap gap-2">
        <a
          href={adres}
          target="_blank"
          rel="noopener noreferrer"
          className="text-marka-700 min-h-dokunma inline-flex items-center text-sm font-medium underline"
        >
          Instagram'ı aç
        </a>
        <Buton
          disabled={gonderiliyor}
          onClick={() => {
            setGonderiliyor(true);
            setHata(null);
            instagramTesekkuru()
              .then(onBitti)
              .catch((e: unknown) => setHata(hataMetni(e, "Şu an eklenemedi.")))
              .finally(() => setGonderiliyor(false));
          }}
        >
          Takip ettim
        </Buton>
      </div>
      {hata && (
        <p role="alert" className="text-hata-700 mt-1 text-xs">
          {hata}
        </p>
      )}
    </section>
  );
}

/** "Nasıl kazanılır" — sayılar sabit değil, kural sürümünden (F-GAM-03). */
function NasilKazanilir({ k }: { k: OyunKurali }) {
  const esikler = [...k.hafta.esikler].sort((a, b) => a.oran - b.oran);
  return (
    <section
      aria-labelledby="puan-nasil"
      className="rounded-kart border-notr-200 bg-notr-0 shadow-kart border p-3"
    >
      <h3 id="puan-nasil" className="text-notr-900 mb-1 text-sm font-semibold">
        Nasıl kazanılır?
      </h3>
      <p className="text-notr-600 mb-2 text-xs">
        Puan, girdiğin veriye değil yaptığın çalışmaya verilir: çözdüğün sorular ve çalıştığın süre.
        Netine ya da sıralamana bakılmaz.
      </p>
      <ul className="text-notr-700 list-disc space-y-1 pl-5 text-xs">
        <li>
          Hedefine ulaştığın görev: {k.gorev.tam} puan; hedefin bir kısmı: {k.gorev.az} puan. Ertesi
          gün girersen yarısı ({k.gorev.gecTam} / {k.gorev.gecAz}).
        </li>
        <li>
          Hedefi olmayan görevde en az {k.gorev.hedefsizTamSoru} soru ya da {k.gorev.tamSureDk}{" "}
          dakika çalışma tam puan alır.
        </li>
        <li>Bir günde görevlerden en çok {k.gorev.gunTavani} puan.</li>
        <li>
          Haftanın görevlerinin{" "}
          {esikler.map((e, i) => (
            <span key={e.oran}>
              {i > 0 ? ", " : ""}%{Math.round(e.oran * 100)}'ini tam yaparsan {e.puan}
            </span>
          ))}{" "}
          puan (en az {k.hafta.haftaAsgariGorev} görevli haftada, yalnız en yüksek olanı).
        </li>
        <li>Sana atanan denemeye katılırsan {k.deneme.puan} puan.</li>
        <li>Koçun özel bir hedefin için puan verebilir.</li>
        <li>
          Puanlar hafta boyunca bekler, Pazartesi kesinleşir. {k.sure.hareketsizAy} ay boyunca hiç
          puan kazanmazsan ya da harcamazsan puanların düşer; {k.sure.bildirimGunOnce} gün önce
          haber veririz.
        </li>
      </ul>
    </section>
  );
}
